'use client'

import { Label, RadioGroup as AriaRadioGroup } from 'react-aria-components'

import type { PublicInputProps } from '../../_shared/AbstractInput.client'
import { AbstractInput } from '../../_shared/AbstractInput.client'
import { twMerge } from '@/lib/classname/twMerge'
import { ValidationMessage } from '../../validation-message/ValidationMessage.client'
import { RadioButton } from './RadioButton.client'

type RadioGroupOwnProps = {
  className?: string
  disabled?: boolean
  id?: string
  options?: { value: string; label: React.ReactNode; description?: React.ReactNode }[]
  isSubmitting?: boolean
  isLoading?: boolean
  isRequired?: boolean
  'data-testid'?: string
}
export type RadioGroupProps = PublicInputProps<RadioGroupOwnProps, 'required'>

export function RadioGroup({
  className,
  label,
  name,
  options,
  disabled,
  id,
  'data-testid': dataTestId,
  ...rules
}: RadioGroupProps) {
  return (
    <AbstractInput
      {...rules}
      name={name}
      render={({ field, fieldState, formState }) => (
        <AriaRadioGroup
          id={id}
          data-testid={dataTestId}
          name={name}
          value={field.value}
          onChange={field.onChange}
          isRequired={fieldState.isRequired}
          isDisabled={field.isDisabled || disabled}
          aria-label={label ? undefined : name}
          aria-describedby={fieldState.error?.message ? `${name}-error` : undefined}
          className={twMerge(
            className,
            (formState.isSubmitting || field.isDisabled) && 'pointer-events-none',
          )}
        >
          {label && (
            <Label className={twMerge('font-bold')}>
              {label}
              {fieldState.isRequired && ' *'}
            </Label>
          )}
          {options?.map((option) => (
            <RadioButton
              key={option.value}
              name={name}
              isSubmitting={formState.isSubmitting}
              isLoading={fieldState.isValidating}
              id={id ? `${id}-${option.value}` : undefined}
              data-testid={dataTestId ? `${dataTestId}-${option.value}` : undefined}
              isDisabled={disabled}
              value={option.value}
              onChange={field.onChange}
              label={option.label}
              description={option.description}
              error={fieldState.error ? { message: fieldState.error.message } : undefined}
            />
          ))}

          {fieldState.error?.message && (
            <ValidationMessage id={`${name}-error`} status="error">
              {fieldState.error?.message ?? ''}
            </ValidationMessage>
          )}
        </AriaRadioGroup>
      )}
    />
  )
}
